import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ArrowRight,
  TrendingUp,
  Users,
  Target,
  BarChart3,
  CheckCircle,
  Clock,
  Building,
} from "lucide-react";

const CaseStudiesPage = () => {
  const caseStudies = [
    {
      id: 1,
      title: "E-Commerce Lead Generation Transformation",
      industry: "E-Commerce",
      challenge: "Inconsistent lead flow and high customer acquisition costs were limiting growth.",
      approach: "Implemented predictive lead scoring, automated email sequences, and dynamic website personalization.",
      results: [
        { metric: "247%", label: "Increase in qualified leads" },
        { metric: "52%", label: "Reduction in cost per acquisition" },
        { metric: "38%", label: "Improvement in conversion rate" },
        { metric: "6 mo", label: "Payback on implementation" },
      ],
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800",
    },
    {
      id: 2,
      title: "B2B Marketing Automation Success",
      industry: "Professional Services",
      challenge: "Sales team overwhelmed with unqualified leads and manual follow-up processes.",
      approach: "Built comprehensive lead scoring and routing system with automated nurture sequences.",
      results: [
        { metric: "180%", label: "Increase in sales productivity" },
        { metric: "65%", label: "Faster lead qualification" },
        { metric: "42%", label: "Improvement in close rate" },
        { metric: "3x", label: "Increase in MQLs" },
      ],
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
    },
    {
      id: 3,
      title: "Content Marketing Scale-Up",
      industry: "SaaS",
      challenge: "Unable to produce enough quality content to support aggressive growth targets.",
      approach: "Implemented AI-assisted content creation, SEO optimization, and distribution automation.",
      results: [
        { metric: "5x", label: "Increase in content output" },
        { metric: "312%", label: "Growth in organic traffic" },
        { metric: "89%", label: "Improvement in SEO rankings" },
        { metric: "156%", label: "Increase in content-attributed revenue" },
      ],
      image: "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800",
    },
  ];

  const methodologySteps = [
    {
      step: "01",
      title: "Discovery & Audit",
      description: "We analyze your current state, identify gaps, and map opportunities for improvement.",
    },
    {
      step: "02",
      title: "Strategy Development",
      description: "Create a custom roadmap with clear milestones, KPIs, and expected outcomes.",
    },
    {
      step: "03",
      title: "System Implementation",
      description: "Build and deploy growth systems with your team's input and approval at each stage.",
    },
    {
      step: "04",
      title: "Optimization & Scale",
      description: "Continuously monitor, test, and improve for compounding results over time.",
    },
  ];

  return (
    <div className="pt-32" data-testid="case-studies-page">
      <SEO 
        title="Case Studies | SEO & Marketing Results"
        description="See how OptimaGrowth has achieved 247% lead increases and 52% cost reductions through SEO, marketing automation, and AI-powered growth systems."
        keywords="marketing automation case studies, SEO results, lead generation case study, digital marketing ROI, growth strategy success stories"
      />
      <Breadcrumb items={[{ name: "Case Studies" }]} />
      {/* Hero Section */}
      <section className="section pb-0" data-testid="case-studies-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">Case Studies</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                SEO & Marketing Automation Case Studies
              </h1>
              <p className="text-lg text-slate-600">
                See how we've helped businesses like yours achieve measurable growth through systematic approaches and proven strategies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results Preview */}
      <section className="section" data-testid="results-preview">
        <div className="container-custom">
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-optima-blue/10 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-optima-blue" />
              </div>
              <div className="font-heading text-3xl font-bold text-optima-dark">247%</div>
              <div className="text-slate-600">Avg. Lead Increase</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-optima-cyan/10 flex items-center justify-center">
                <Target className="w-8 h-8 text-optima-cyan" />
              </div>
              <div className="font-heading text-3xl font-bold text-optima-dark">52%</div>
              <div className="text-slate-600">Cost Reduction</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-optima-pink/10 flex items-center justify-center">
                <Clock className="w-8 h-8 text-optima-pink" />
              </div>
              <div className="font-heading text-3xl font-bold text-optima-dark">6 mo</div>
              <div className="text-slate-600">Avg. Payback Period</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Building className="w-8 h-8 text-green-600" />
              </div>
              <div className="font-heading text-3xl font-bold text-optima-dark">50+</div>
              <div className="text-slate-600">Businesses Helped</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-alt" data-testid="case-studies-list">
        <div className="container-custom">
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-soft overflow-hidden"
                data-testid={`case-study-${study.id}`}
              >
                <div className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 1 ? "lg:grid-flow-dense" : ""}`}>
                  <div className={`${index % 2 === 1 ? "lg:col-start-2" : ""}`}>
                    <img
                      src={study.image}
                      alt={`${study.title} - marketing automation and SEO case study results`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-8 lg:p-12">
                    <span className="badge badge-cyan mb-4">{study.industry}</span>
                    <h3 className="font-heading text-2xl font-bold text-optima-dark mb-4">
                      {study.title}
                    </h3>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-optima-dark mb-2">Challenge</h4>
                      <p className="text-slate-600">{study.challenge}</p>
                    </div>
                    
                    <div className="mb-6">
                      <h4 className="font-semibold text-optima-dark mb-2">Our Approach</h4>
                      <p className="text-slate-600">{study.approach}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {study.results.map((result) => (
                        <div key={result.label} className="bg-slate-50 rounded-lg p-4">
                          <div className="font-heading text-2xl font-bold text-optima-blue">
                            {result.metric}
                          </div>
                          <div className="text-sm text-slate-600">{result.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="section" data-testid="methodology-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-blue mb-4">Our Methodology</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
              How We Deliver Results
            </h2>
            <p className="text-slate-600 text-lg">
              A proven, systematic approach that ensures success at every stage.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodologySteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-5xl font-heading font-bold text-optima-blue/20 mb-4">
                  {step.step}
                </div>
                <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">
                  {step.title}
                </h3>
                <p className="text-slate-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New Company Note */}
      <section className="section-alt" data-testid="new-company-note">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-optima-blue to-optima-cyan rounded-2xl p-8 lg:p-12 text-white">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <h3 className="font-heading text-2xl font-bold mb-4">
                  Building Our Case Study Library
                </h3>
                <p className="text-white/90">
                  As a growing company, we're actively building our portfolio of documented case studies. The results above represent our methodology in action. We'd love to add your success story to our collection.
                </p>
              </div>
              <div className="text-center lg:text-right">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
                  data-testid="case-studies-cta"
                >
                  Become a Case Study <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="case-studies-final-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Be Our Next Success Story?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Book a free growth audit and let's discuss how we can achieve similar results for your business.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="case-studies-book-call"
            >
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Services</Link>
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing</Link>
              <Link to="/software" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Our Software</Link>
              <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Our Blog</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudiesPage;
